import { Icon } from "@iconify/react/dist/iconify.js";
import { useState } from "react";

const helpData = [
    {
        id: 1,
        title: "Access and Authentication",
        description: "Solutions for login problems, session management, and authentication setup.",
        icon: "solar:shield-keyhole-bold",
        common_problems: [
            "Login Difficulties: Users unable to access the portal due to incorrect credentials or forgotten passwords.​",
            "Session Expiry: Unexpected logouts or session timeouts disrupting workflow.​",
            "Multi-Factor Authentication (MFA) Challenges: Issues setting up or using MFA for enhanced security."
        ],
        solutions: [
            "Password Reset Procedures: Step-by-step guide to resetting passwords securely.​",
            "Session Management Tips: Recommendations to prevent session expirations and maintain active sessions.​",
            "MFA Setup Assistance: Detailed instructions for configuring and troubleshooting MFA."
        ]
    },
    {
        id: 2,
        title: "User Management",
        description: "Guidance on adding, removing, and managing user roles and permissions.",
        icon: "fa6-solid:users-gear",
        common_problems: [
            "Adding/Removing Users: Difficulties in managing user accounts within the portal.​​",
            "Assigning Roles and Permissions: Uncertainty about how to allocate appropriate access levels.​​",
            "Handling Orphaned Accounts: Risks associated with inactive or unmonitored admin accounts."
        ],
        solutions: [
            "User Account Management Guide: Instructions for creating, modifying, and deleting user accounts.​​",
            "Role Assignment Best Practices: Strategies for assigning roles to ensure security and efficiency.​​",
            "Regular Audit Recommendations: Encouragement to periodically review user accounts to prevent unauthorized access."
        ]
    },
    {
        id: 3,
        title: "Data Management and Reporting",
        description: "Help with exporting, verifying, and generating accurate data reports.",
        icon: "ix:data-management-filled",
        common_problems: [
            "Data Export Issues: Challenges in exporting reports or data from the portal.​​",
            "Data Accuracy Concerns: Discrepancies or doubts about the reliability of displayed data.​​",
            "Custom Report Generation: Difficulties in creating tailored reports to meet specific needs."
        ],
        solutions: [
            "Data Export Tutorials: Guides on how to export data in various formats.​​",
            "Data Verification Steps: Methods to cross-verify data accuracy within the portal.​​",
            "Custom Reporting Walkthroughs: Instructions for utilizing portal tools to generate customized reports."
        ]
    },
    {
        id: 4,
        title: "System Performance and Downtime",
        description: "Information on improving portal responsiveness and managing system outages.",
        icon: "mingcute:performance-fill",
        common_problems: [
            "Slow Portal Response: Lagging or unresponsive portal behavior affecting productivity.​​",
            "Unexpected Downtime: Inability to access the portal due to server issues or maintenance.​​",
            "Error Messages: Encountering ambiguous or unclear error notifications."
        ],
        solutions: [
            "Performance Optimization Tips: Suggestions to enhance portal responsiveness, such as clearing browser cache or using recommended browsers.​​",
            "Downtime Notifications: Information on how to stay informed about scheduled maintenance or unexpected outages.​​",
            "Error Resolution Guide: Decoding common error messages and steps to resolve them."
        ]
    },
    {
        id: 5,
        title: "Security and Compliance",
        description: "Best practices and guidelines to ensure secure and compliant portal usage.",
        icon: "mingcute:file-security-fill",
        common_problems: [
            "Detecting and responding to potential security breaches.​​",
            "Ensuring portal usage aligns with legal and organizational data policies.​​",
            "Locating and interpreting logs of user activities for security reviews."
        ],
        solutions: [
            "Recommendations for creating strong passwords, recognizing phishing attempts, and safeguarding login credentials.​​",
            "Overview of relevant data protection regulations and how the portal supports compliance.​​",
            "Steps to retrieve and analyze user activity logs within the portal."
        ]
    },
    {
        id: 6,
        title: "Feature Usage and Optimization",
        description: "Instructions for effectively using, customizing, and integrating portal features.",
        icon: "fluent:data-usage-edit-24-filled",
        common_problems: [
            "Underutilization of Portal Features: Not leveraging the full range of available tools and functionalities.​​",
            "Customization Challenges: Difficulties in tailoring the portal interface to individual or organizational preferences.​​",
            "Integration with Other Systems: Issues connecting the portal with external applications or services."
        ],
        solutions: [
            "Feature Highlight Tutorials: Regular updates showcasing different features and their benefits.​​",
            "Customization Guides: Instructions for personalizing dashboards, notifications, and other settings.​​",
            "Integration Support: Resources and contact information for assistance with third-party integrations.​"
        ]
    },
]

const HelpContent = () => {

    const [expandedItem, setExpandedItem] = useState(null);

    const toggleItem = (id) => {
        setExpandedItem(prev => (prev === id ? null : id));
    }

    return (
        <div className={`grid gap-4 w-full grid-cols-1 lg:grid-cols-2
        ${expandedItem ? '' : ''}`}>
            {helpData.map((item) => (
                // Empty class variables to modify in 3rd stage of Design
                <div key={item.id} className={`flex flex-col w-full gap-3 transition-all duration-300 ease-out-in ${expandedItem === item.id ? '' : 'hover:scale-[1.02]'} `}>
                    <div
                        id="help-card"
                        className={`flex flex-row items-center w-full justify-between p-4 rounded-md
                            ${expandedItem === item.id ? 'tag-primary' : 'card-container'}
                            `}
                        onClick={() => toggleItem(item.id)}
                    >
                        <div className="flex flex-row items-center gap-3">
                            <Icon icon={item.icon} className={`text-secondary ${item.id === 2 ? 'text-4xl' : 'text-5xl'}`}></Icon>
                            <div className="flex flex-col items-start gap-1">
                                <h5 className="text-lg font-semibold">{item.title}</h5>
                                <p className="text-base text-font-secondary">{item.description}</p>
                            </div>
                        </div>
                        <Icon icon={expandedItem === item.id ? 'mingcute:up-line' : 'mingcute:down-line'} className="text-4xl text-secondary"></Icon>
                    </div>
                    {expandedItem === item.id && (
                            <div id="help-details" className="grid grid-cols-1 lg:grid-cols-2 items-stretch gap-3 p-4 card-container rounded-md">
                            
                                <div className="flex flex-col gap-3">
                                    <h6 className="text-red-500 w-full text-lg flex flex-row p-2 tag-red justify-center items-center gap-1"><Icon icon="material-symbols-light:problem" className="text-2xl" /> <span className="text-font-primary">Common Problems</span></h6>
                                    <ul className="text-font-secondary text-base space-y-3">
                                        {item.common_problems.map((problem) => (
                                            <li className="text-font-primary">{problem}</li>
                                        ))}
                                    </ul>
                                </div>
                                
                                <div className="flex flex-col gap-3">
                                    <h6 className="text-secondary w-full text-lg flex flex-row p-2 tag-secondary justify-center items-center gap-1"><Icon icon="line-md:check-all" className="text-2xl" /> <span className="text-font-primary">Solutions</span></h6>
                                    <ul className="text-font-secondary text-base space-y-3">
                                        {item.solutions.map((solution) => (
                                            <li className="text-font-primary">{solution}</li>
                                        ))}
                                    </ul>
                                </div>
                            
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

export default HelpContent;